type PortalType = 'admin' | 'provider' | 'customer';

type AppConfig = {
  app: {
    env: string;
    name: string;
    portal: PortalType;
    poviderPortalUrl: string;
    homePage: string;
    isProduction: boolean;
    adminPortalUrl: string;
    allowedRoles: string[];
  };
  api: {
    baseUrl: string;
    userServiceEndpoint: string;
    productServiceEndpoint: string;
    paymentServiceEndpoint: string;
    userApiV1Url: string;
    productApiV1Url: string;
    apiVersion: string;
    timeout: number;
  };
  auth: {
    cookieDomain: string;
    withCredentials: boolean;
  };
  features: {
    enableMFA: boolean;
    enableNewBookingFlow: boolean;
  };
  unleash: {
    url: string;
    clientKey: string;
    appName: string;
    refreshInterval: number;
    /**
     * False when either `url` or `clientKey` is missing. Vite inlines
     * `import.meta.env` at build time, so a var absent from the Amplify console
     * is baked in as `undefined` (see ./env.ts). The Unleash client THROWS
     * ('url is required' / 'clientKey is required') when constructed without
     * them, which would white-screen the whole site — so main.tsx only mounts
     * FlagProvider when this is true.
     */
    isConfigured: boolean;
  };
};

const NODE_ENV = import.meta.env.VITE_NODE_ENV;
const isProduction = NODE_ENV === 'production';

// Unleash frontend (client-side) evaluation — same pattern as
// gigmosaic-provider-dashboard/src/config/appConfig.ts. The clientKey is a
// frontend token; it is scoped server-side to a single Unleash project +
// environment, so which flags this app sees is determined by the token, not by
// anything in this file.
const UNLEASH_URL = import.meta.env.VITE_UNLEASH_FRONTEND_URL;
const UNLEASH_CLIENT_KEY = import.meta.env.VITE_UNLEASH_CLIENT_KEY;

export const appConfig: AppConfig = {
  app: {
    env: import.meta.env.VITE_NODE_ENV,
    isProduction,
    name: 'Public portal',
    portal: 'customer',
    homePage: '/',
    poviderPortalUrl: import.meta.env.VITE_APP_PROVIDER_PORTAL_URL,
    adminPortalUrl: import.meta.env.VITE_APP_ADMIN_PORTAL_URL,
    allowedRoles: ['customer', 'provider', 'admin'],
  },

  api: {
    baseUrl: import.meta.env.VITE_BACKEND_PORT, // e.g., https://api.hirepanther.ca
    userServiceEndpoint: import.meta.env.VITE_APP_BACKEND_USER,
    productServiceEndpoint: import.meta.env.VITE_APP_BACKEND_SERVICE,
    paymentServiceEndpoint: import.meta.env.VITE_BACKEND_PAYMENT_SERVICE,
    apiVersion: import.meta.env.VITE_BACKEND_VERSION,
    userApiV1Url: `${import.meta.env.VITE_BACKEND_USER_SERVICE}/${import.meta.env.VITE_BACKEND_VERSION}`, //	/user/api/v1
    productApiV1Url: `${import.meta.env.VITE_BACKEND_PRODUCT_SERVICE}/${import.meta.env.VITE_BACKEND_VERSION}`, //	/product/api/v1
    timeout: 5000, // 5 seconds
  },

  auth: {
    cookieDomain: import.meta.env.VITE_COOKIE_DOMAIN,
    withCredentials: true,
  },

  features: {
    enableMFA: import.meta.env.VITE_ENABLE_MFA === 'true',
    enableNewBookingFlow: import.meta.env.VITE_ENABLE_NEW_BOOKING === 'true',
  },

  unleash: {
    url: UNLEASH_URL,
    clientKey: UNLEASH_CLIENT_KEY,
    appName: import.meta.env.VITE_UNLEASH_APP_NAME ?? 'customer-portal',
    refreshInterval: 30,
    isConfigured: Boolean(UNLEASH_URL && UNLEASH_CLIENT_KEY),
  },
};
